import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from "react-router-dom";
import ChartHeader from '../components/ChartHeader';
import ChartDetail from '../components/ChartDetail';
import CastSetting from '../components/CastSetting';

function Chart() {
    const search = useLocation().search;
    const chartID: string  = new URLSearchParams(search).get('id') ?? "m1_w1";
    const chartLevel = new URLSearchParams(search).get('level');
    const [chartColors, setChartColors] = useState(["rgb(255, 153, 200)","rgb(252, 246, 189)","rgb(208, 244, 222)","rgb(169, 222, 249)","rgb(228, 193, 249)"]);
    
    // State for Density Heatmap Window (default 10s)
    const [densityWindow, setDensityWindow] = useState(10); 
    
    // State for Max Duration
    const [songMaxDuration, setSongMaxDuration] = useState(180);

    // --- Audio State & Refs ---
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const requestRef = useRef<number>(); // Reference for the animation frame ID

    // Initialize Audio
    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.src = `${process.env.PUBLIC_URL}/songs/${chartID}.ogg`;
            audioRef.current.load();
        }
    }, [chartID]);

    // Animation Loop
    const animate = () => {
        if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
        }
        requestRef.current = requestAnimationFrame(animate);
    };

    // Handle Play/Pause
    useEffect(() => {
        if (isPlaying) {
            requestRef.current = requestAnimationFrame(animate);
        } else {
            if (requestRef.current) {
                cancelAnimationFrame(requestRef.current);
            }
        }
        return () => {
            if (requestRef.current) {
                cancelAnimationFrame(requestRef.current);
            }
        };
    }, [isPlaying]);

    const togglePlay = () => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleSkip = (seconds: number) => {
        if (audioRef.current) {
            const newTime = Math.min(Math.max(audioRef.current.currentTime + seconds, 0), audioRef.current.duration || songMaxDuration);
            audioRef.current.currentTime = newTime;
            setCurrentTime(newTime); // Update immediately for responsiveness
        }
    };

    return (
        <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
            <ChartHeader />
            
            <audio 
                ref={audioRef} 
                onEnded={() => setIsPlaying(false)}
            />

            <CastSetting 
                colors={chartColors} 
                setChartColors={setChartColors} 
                densityWindow={densityWindow} 
                setDensityWindow={setDensityWindow} 
                maxDuration={songMaxDuration}
                isPlaying={isPlaying}
                togglePlay={togglePlay}
                onSkip={handleSkip}
            />
            
            <div className="flex-grow overflow-hidden relative">
                <ChartDetail 
                    id={chartID + '_' + chartLevel} 
                    colors={chartColors} 
                    densityWindow={densityWindow} 
                    setMaxDuration={setSongMaxDuration}
                    currentTime={currentTime}
                />
            </div>
        </div>
    );
}

export default Chart;